import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieConsentBanner from "./components/CookieConsentBanner";

import LibraryPage from "./pages/LibraryPage";
import TopicPage from "./pages/TopicPage";
import CategoryPage from "./pages/CategoryPage";
import SearchPage from "./pages/SearchPage";
import SourceVaultPage from "./pages/SourceVaultPage";
import MasterTimelinePage from "./pages/MasterTimelinePage";
import EntitiesIndexPage from "./pages/EntitiesIndexPage";
import EntityPage from "./pages/EntityPage";
import SavedPage from "./pages/SavedPage";
import AboutPage from "./pages/AboutPage";
import MethodologyPage from "./pages/MethodologyPage";
import LoginPage from "./pages/admin/LoginPage";
import MagicLinkLoginPage, { MagicLinkVerifyPage } from "./pages/MemberLoginPage";
import AccountLoginPage from "./pages/AccountLoginPage";
import AccountSignupPage from "./pages/AccountSignupPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingQuizPage from "./pages/OnboardingQuizPage";
import ActivateAccountPage from "./pages/ActivateAccountPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MembershipPage from "./pages/MembershipPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTopicEditor from "./pages/admin/AdminTopicEditor";
import AdminCSV from "./pages/admin/AdminCSV";
import AdminMembers from "./pages/admin/AdminMembers";
import AdminWebhooks from "./pages/admin/AdminWebhooks";
import AdminHealth from "./pages/admin/AdminHealth";
import AdminSearchIntel from "./pages/admin/AdminSearchIntel";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminVaccines from "./pages/admin/AdminVaccines";
import VaccinesHubPage from "./pages/vaccines/VaccinesHubPage";
import CountrySchedulePage from "./pages/vaccines/CountrySchedulePage";
import AgeCohortPage from "./pages/vaccines/AgeCohortPage";
import VaccinePage from "./pages/vaccines/VaccinePage";
import EntityRouter from "./pages/vaccines/EntityRouter";
import ComparisonPage from "./pages/vaccines/ComparisonPage";
import NotFoundPage from "./pages/NotFoundPage";
import ScrollToTop from "./components/ScrollToTop";
import { SourceReaderProvider } from "./components/SourceReader";

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <SourceReaderProvider>
                    <Routes>
                        <Route path="/" element={<LibraryPage />} />
                        <Route path="/topic/:slug" element={<TopicPage />} />
                        <Route path="/category/:name" element={<CategoryPage />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/sources" element={<SourceVaultPage />} />
                        <Route path="/timeline" element={<MasterTimelinePage />} />
                        <Route path="/entities" element={<EntitiesIndexPage />} />
                        <Route path="/entity/:slug" element={<EntityRouter />} />
                        <Route path="/saved" element={<SavedPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/methodology" element={<MethodologyPage />} />

                        {/* Phase 3 — Vaccination Research Explorer */}
                        <Route path="/vaccines" element={<VaccinesHubPage />} />
                        <Route path="/vaccines/uk" element={<CountrySchedulePage />} />
                        <Route path="/vaccines/us" element={<CountrySchedulePage />} />
                        <Route path="/vaccines/by-age/:slug" element={<AgeCohortPage />} />
                        <Route path="/vaccines/v/:slug" element={<VaccinePage />} />
                        <Route path="/vaccines/compare/:pair" element={<ComparisonPage />} />
                        {/* Avoid route param collision: pass country via the same component */}
                        <Route path="/vaccines/country/:country" element={<CountrySchedulePage />} />

                        {/* Phase 6B — magic-link member login (kept as fallback) */}
                        <Route path="/login" element={<MagicLinkLoginPage />} />
                        <Route path="/auth/verify" element={<MagicLinkVerifyPage />} />
                        <Route path="/auth/check-your-email" element={<MagicLinkLoginPage />} />

                        {/* Member-1 — Shopify Customer Accounts (primary) */}
                        <Route path="/account/login" element={<AccountLoginPage />} />
                        <Route path="/account/signup" element={<AccountSignupPage />} />
                        <Route path="/welcome" element={<OnboardingQuizPage />} />
                        <Route path="/activate-account" element={<ActivateAccountPage />} />
                        {/* Legacy alias — preserved so any in-flight URLs from earlier
                            Shopify emails / shares continue to work. Same component. */}
                        <Route path="/welcome-back" element={<ActivateAccountPage />} />
                        <Route path="/account/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/membership" element={<MembershipPage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />

                        <Route path="/admin/login" element={<LoginPage />} />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/topics/new"
                            element={
                                <ProtectedRoute>
                                    <AdminTopicEditor />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/topics/:id/edit"
                            element={
                                <ProtectedRoute>
                                    <AdminTopicEditor />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/csv"
                            element={
                                <ProtectedRoute>
                                    <AdminCSV />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/members"
                            element={
                                <ProtectedRoute>
                                    <AdminMembers />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/webhooks"
                            element={
                                <ProtectedRoute>
                                    <AdminWebhooks />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/health"
                            element={
                                <ProtectedRoute>
                                    <AdminHealth />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/search-intel"
                            element={
                                <ProtectedRoute>
                                    <AdminSearchIntel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/analytics"
                            element={
                                <ProtectedRoute>
                                    <AdminAnalytics />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/vaccines"
                            element={
                                <ProtectedRoute>
                                    <AdminVaccines />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </SourceReaderProvider>
                </BrowserRouter>
                <Toaster theme="dark" position="bottom-right" />
                <CookieConsentBanner />
            </AuthProvider>
        </div>
    );
}

export default App;
